import { Dropdown, Field as DropField, Item, Menu, Select } from "@zendeskgarden/react-dropdowns";
import { Checkbox, Field, Input, Label, Message, Radio, Textarea } from "@zendeskgarden/react-forms";
import React from "react";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { t } from "../../../../translations/translation.service";
import style from "../index.module.scss";

export interface PreScreeningQuestion {
  questionId?: string;
  text: string;
  type: string;
  required: boolean;
  options: string[];
  display?: { text: string; options: string[] };
}

export interface PreScreeningAnswer {
  questionId?: string;
  questionText: string;
  type: string;
  values: string[];
}

interface Props {
  show: boolean;
  loading: boolean;
  questions: PreScreeningQuestion[];
  initialAnswers?: PreScreeningAnswer[];
  readOnly?: boolean;
  indicadoPor?: string;
  onChangeIndicadoPor?: (value: string) => void;
  onCancel: () => void;
  onSubmit: (answers: PreScreeningAnswer[]) => void;
}

interface State {
  values: string[][];
  error: string;
}

const MAX_TEXT_ANSWER_LENGTH = 750;

function questionLabel(question: PreScreeningQuestion): string {
  return question.display?.text || question.text;
}

function optionLabel(question: PreScreeningQuestion, option: string): string {
  const index = question.options.indexOf(option);
  return (index >= 0 && question.display?.options?.[index]) || option;
}

class PreScreeningModal extends React.Component<Props, State> {
  state: State = {
    values: [],
    error: "",
  };

  constructor(props: Props) {
    super(props);
    this.submit = this.submit.bind(this);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.show && !prevProps.show) {
      this.setState({ values: this.initialValues(), error: "" });
    }
  }

  initialValues(): string[][] {
    const initialAnswers = this.props.initialAnswers || [];
    return this.props.questions.map((question) => {
      const answer =
        (question.questionId &&
          initialAnswers.find((item) => item.questionId === question.questionId)) ||
        initialAnswers.find(
          (item) => item.questionText.trim().toLowerCase() === question.text.trim().toLowerCase()
        );
      return answer ? [...answer.values] : [];
    });
  }

  setValue(index: number, values: string[]) {
    this.setState((prevState) => ({
      values: prevState.values.map((current, i) => (i === index ? values : current)),
    }));
  }

  toggleOption(index: number, option: string) {
    this.setState((prevState) => ({
      values: prevState.values.map((current, i) => {
        if (i !== index) return current;
        return current.includes(option)
          ? current.filter((value) => value !== option)
          : [...current, option];
      }),
    }));
  }

  submit() {
    const missing = this.props.questions.some(
      (question, index) =>
        question.required &&
        !(this.state.values[index] || []).some((value) => value.trim())
    );

    if (missing) {
      this.setState({ error: t("PreTriagemCamposObrigatorios") });
      return;
    }

    this.setState({ error: "" });
    this.props.onSubmit(
      this.props.questions.map((question, index) => ({
        questionId: question.questionId,
        questionText: question.text,
        type: question.type,
        values: (this.state.values[index] || []).filter((value) => value.trim()),
      }))
    );
  }

  renderAnswer(question: PreScreeningQuestion, index: number) {
    const values = this.state.values[index] || [];
    const readOnly = Boolean(this.props.readOnly);

    if (question.type === "text") {
      const value = values[0] || "";
      return (
        <>
          <Textarea
            value={value}
            maxLength={MAX_TEXT_ANSWER_LENGTH}
            minRows={3}
            maxRows={8}
            readOnly={readOnly}
            onChange={(event) => this.setValue(index, [event.target.value])}
          />
          <span className={style["counter"]}>{`${value.length}/${MAX_TEXT_ANSWER_LENGTH}`}</span>
        </>
      );
    }

    if (question.type === "combo") {
      return (
        <Dropdown
          selectedItem={values[0] || null}
          onSelect={(option: string) => this.setValue(index, [option])}
        >
          <DropField>
            <Select disabled={readOnly}>
              {values[0] ? optionLabel(question, values[0]) : t("PreTriagemSelecione")}
            </Select>
          </DropField>
          <Menu>
            {question.options.map((option) => (
              <Item key={option} value={option}>
                {optionLabel(question, option)}
              </Item>
            ))}
          </Menu>
        </Dropdown>
      );
    }

    if (question.type === "single_select") {
      return (
        <>
          {question.options.map((option) => (
            <Field key={option}>
              <Radio
                name={`pre-screening-${index}`}
                value={option}
                checked={values[0] === option}
                disabled={readOnly}
                onChange={() => this.setValue(index, [option])}
              >
                <Label>{optionLabel(question, option)}</Label>
              </Radio>
            </Field>
          ))}
        </>
      );
    }

    return (
      <>
        {question.options.map((option) => (
          <Field key={option}>
            <Checkbox
              checked={values.includes(option)}
              disabled={readOnly}
              onChange={() => this.toggleOption(index, option)}
            >
              <Label>{optionLabel(question, option)}</Label>
            </Checkbox>
          </Field>
        ))}
      </>
    );
  }

  render() {
    return (
      <Modal
        show={this.props.show}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
        keyboard={false}
        onHide={() => undefined}
      >
        <Modal.Header>
          <h5 className="m-0">{t("FormularioCandidatura")}</h5>
        </Modal.Header>
        <Modal.Body>
          <p>{t(this.props.readOnly ? "PreTriagemSomenteLeitura" : "PreTriagemInstrucao")}</p>
          {this.props.questions.map((question, index) => (
            <Field className="mb-3" key={`${question.type}-${question.text}`}>
              <Label>
                {questionLabel(question)}
                {question.required && " *"}
              </Label>
              {this.renderAnswer(question, index)}
            </Field>
          ))}
          {this.props.onChangeIndicadoPor && !this.props.readOnly && (
            <Field className="mb-3">
              <Label>{t("Alguém te indicou para esta vaga?")}</Label>
              <Input
                value={this.props.indicadoPor || ""}
                onChange={(event) => this.props.onChangeIndicadoPor?.(event.target.value)}
                isCompact
              />
            </Field>
          )}
          {this.state.error && <Message validation="error">{this.state.error}</Message>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className={style["cancel-modal-button"]}
            disabled={this.props.loading}
            onClick={this.props.onCancel}
          >
            {t(this.props.readOnly ? "Fechar" : "Cancelar")}
          </Button>
          {!this.props.readOnly && (
            <Button
              className={style["confirm-modal-button"]}
              disabled={this.props.loading}
              onClick={this.submit}
            >
              {t("EnviarFormulario")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default PreScreeningModal;
